import { FC, useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Bell, Clock } from "lucide-react"
import { Badge } from "./ui/badge"
import type { Alarm } from "@/gen/api"
import { Button } from "./ui/button"
import { cn } from "@/lib/utils"

interface AlarmsProps {
  alarms: Alarm[]
}

const formatAlarmTime = (value?: string) => {
  if (!value) return "Unknown time"

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  return new Intl.DateTimeFormat("en-AU", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date)
}

const getRelativeTime = (value?: string) => {
  if (!value) return "Unknown time"

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value

  const diffMs = Date.now() - date.getTime()
  const absDiffMs = Math.abs(diffMs)
  const minutes = Math.round(absDiffMs / 60000)
  const hours = Math.round(absDiffMs / 3600000)
  const days = Math.round(absDiffMs / 86400000)

  const suffix = diffMs >= 0 ? "ago" : "from now"

  if (minutes < 1) return "Just now"
  if (minutes < 60) return `${minutes}m ${suffix}`
  if (hours < 24) return `${hours}h ${suffix}`
  return `${days}d ${suffix}`
}

const getAlarmTime = (alarm: Alarm) => alarm.timestamp ?? alarm.time

const getBriefMessage = (alarm: Alarm) => {
  if (alarm.display?.text) return alarm.display.text

  const meterText = `Your smart water meter${alarm.serialNumber ? ` ${alarm.serialNumber}` : ""}`
  const atProperty = alarm.address ? ` at your property ${alarm.address}` : ""

  return `${meterText}${atProperty} has recorded a continuous water usage over the last 24 hours.`
}

const getAlarmTitle = (alarm: Alarm) => alarm.display?.title || "Potential Leak Detected"

const getFullMessage = (alarm: Alarm) => {
  const time = formatAlarmTime(getAlarmTime(alarm))

  return (
    alarm.display?.detailText ||
    alarm.display?.text ||
    alarm.message ||
    alarm.text ||
    `Your smart water meter has triggered a 24-hour continuous flow alert on ${time}. Continuous water use could be something as minor as a dripping tap or a concealed leak.`
  )
}

const Alarms: FC<AlarmsProps> = ({ alarms }) => {
  const [expandedAlarms, setExpandedAlarms] = useState<Set<string>>(new Set())

  const toggleExpand = (id: string) => {
    setExpandedAlarms((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-y-0 px-6 py-4">
        <div className="flex items-center gap-2">
          <CardTitle className="text-lg font-black tracking-tight text-slate-900 uppercase">
            Alarms
          </CardTitle>
          {alarms.length > 0 && (
            <Badge className="bg-red-600 px-2 py-0.5 text-xs font-black">
              {alarms.length} Active
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex-1 px-4 pt-2 pb-4">
        {alarms.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground">
            <Bell className="mx-auto mb-2 size-10 opacity-20" />
            <p>No active alerts</p>
          </div>
        ) : (
          <div className="custom-scrollbar max-h-[500px] space-y-3 pr-1">
            {alarms.map((alarm, index) => {
              const alarmId = alarm.id ?? `${alarm.type}-${alarm.timestamp}-${index}`
              const isExpanded = expandedAlarms.has(alarmId)
              const title = getAlarmTitle(alarm)

              return (
                <div
                  key={alarmId}
                  className={cn(
                    "group relative flex cursor-pointer overflow-hidden rounded-lg border-2 border-slate-200 bg-white transition-all hover:border-primary hover:shadow-md",
                  )}
                >
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-start p-4">
                      <div className="mt-1 mr-4 rounded-full bg-white p-2 shadow-sm">
                        <Bell className="size-5 text-primary" />
                      </div>

                      <div className="flex-1 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-base font-black tracking-tight text-slate-900 uppercase">
                            {title}
                          </p>
                          <div className="flex shrink-0 items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-600">
                            <Clock className="mr-1 size-3" />
                            {getRelativeTime(getAlarmTime(alarm))}
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <p className="text-sm leading-relaxed font-medium text-slate-700">
                            {getBriefMessage(alarm)}
                          </p>
                        </div>

                        <div className="pt-1">
                          <Button
                            variant="link"
                            size="sm"
                            className="h-auto p-0 font-bold text-primary hover:text-primary/80"
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleExpand(alarmId)
                            }}
                          >
                            {isExpanded ? "Show less" : "Show more"}
                          </Button>
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="border-t border-slate-100 bg-slate-50 p-4">
                        <p className="text-sm leading-relaxed font-medium text-slate-600 whitespace-pre-line">
                          {getFullMessage(alarm)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default Alarms
